import { keepPreviousData } from "@tanstack/react-query";
import type {
  GetUsers,
  GetUserEmployment,
  PutUserEmployment,
  PostUserCostRate,
  GetUserHasPin,
  PostUserPin,
  PostVerifyUserPin,
  GetUserPermissions,
  GetUserServiceCustomValues,
  PutUserServiceCustomValues,
  PostSendForgotPassword,
  PostResetPassword,
  DeleteUser,
  GetUserImage,
  GetUserImageUploadUrl,
  PostCompleteUserImageUpload,
  DeleteUserImage,
} from "@/server-types";
import { get, post, put, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

/**
 * POST /user
 */
export const useUserList = (body?: GetUsers["body"], disabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["userList", body],
    queryFn: () => post<GetUsers>("/user", body ?? {}),
    placeholderData: keepPreviousData,
    retry: true,
    enabled: !disabled,
  });
};

export const useGetUserHasPin = (query: GetUserHasPin["query"]) => {
  return useCustomQuery({
    queryKey: ["user-has-pin", query],
    queryFn: () => get<GetUserHasPin>(`/user/has-pin`, query),
    enabled: !!query.userId,
  });
};

export const usePostUserPIN = () => {
  return useCustomMutation({
    mutationKey: ["post-user-pin"],
    mutationFn: (body: PostUserPin["body"]) =>
      post<PostUserPin>("/user/pin", body),
  });
};

export const useVerifyUserPIN = () => {
  return useCustomMutation({
    mutationKey: ["verify-user-pin"],
    mutationFn: (body: PostVerifyUserPin["body"]) =>
      post<PostVerifyUserPin>("/user/pin/verify", body),
  });
};

export const useGetUserPermissions = ({
  query,
  disabled,
}: {
  query: GetUserPermissions["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["user-permissions", query],
    queryFn: () => get<GetUserPermissions>("/user/permissions", query),
    enabled: !disabled,
  });
};

export const useGetUserServiceCustomValues = ({
  params,
}: {
  params: GetUserServiceCustomValues["params"];
}) => {
  return useCustomQuery({
    queryKey: ["user-service-custom-values", params],
    queryFn: () =>
      get<GetUserServiceCustomValues>(
        `/user/${params.userId}/service/customValues`,
      ),
    enabled: !!params.userId,
  });
};

export const usePutUserServiceCustomValues = ({
  userId,
}: {
  userId: number;
}) => {
  return useCustomMutation({
    mutationKey: ["put-user-service-custom-values", userId],
    mutationFn: (body: PutUserServiceCustomValues["body"]) =>
      put<PutUserServiceCustomValues>(
        `/user/${userId}/service/customValues`,
        body,
      ),
  });
};

export const usePostSendForgotPassword = () => {
  return useCustomMutation({
    mutationKey: ["post-send-forgot-password"],
    mutationFn: (props: { body: PostSendForgotPassword["body"] }) =>
      post<PostSendForgotPassword>("/user/forgot-password/send", props.body),
  });
};

export const usePostResetPassword = () => {
  return useCustomMutation({
    mutationKey: ["post-reset-password"],
    mutationFn: (props: { body: PostResetPassword["body"] }) =>
      post<PostResetPassword>("/user/reset-password", props.body),
  });
};

/**
 * DELETE /user/:userId
 */
export const useDeleteUser = () => {
  return useCustomMutation({
    mutationKey: ["delete-user"],
    mutationFn: ({
      params,
      query,
    }: {
      params: DeleteUser["params"];
      query?: DeleteUser["query"];
    }) => del<DeleteUser>(`/user/${params.userId}`, undefined, query),
  });
};

/**
 * GET /user/:userId/image
 */
export const useGetUserImage = (userId: number | undefined) => {
  return useCustomQuery({
    queryKey: ["userImage", userId],
    queryFn: () => get<GetUserImage>(`/user/${userId ?? -1}/image`),
    enabled: userId != null && userId > 0,
  });
};

/**
 * GET /user/:userId/image/upload
 */
export const useGetUserImageUploadUrl = () => {
  return useCustomMutation({
    mutationKey: ["getUserImageUploadUrl"],
    mutationFn: ({
      userId,
      query,
    }: {
      userId: number;
      query: GetUserImageUploadUrl["query"];
    }) => get<GetUserImageUploadUrl>(`/user/${userId}/image/upload`, query),
  });
};

/**
 * POST /user/:userId/image/upload/complete
 */
export const useCompleteUserImageUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeUserImageUpload"],
    mutationFn: ({
      userId,
      body,
    }: {
      userId: number;
      body: PostCompleteUserImageUpload["body"];
    }) =>
      post<PostCompleteUserImageUpload>(
        `/user/${userId}/image/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["userImage", variables.userId],
      });
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};

/**
 * DELETE /user/:userId/image
 */
export const useDeleteUserImage = () => {
  return useCustomMutation({
    mutationFn: ({ userId }: { userId: number }) =>
      del<DeleteUserImage>(`/user/${userId}/image`),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["userImage", variables.userId],
      });
      queryClient.invalidateQueries({ queryKey: ["userList"] });
    },
  });
};

/**
 * GET /user/:userId/employment
 */
export const useGetUserEmployment = (
  userId: number | undefined,
  { disabled }: { disabled?: boolean } = {},
) => {
  return useCustomQuery({
    queryKey: ["user-employment", userId],
    queryFn: () => get<GetUserEmployment>(`/user/${userId ?? -1}/employment`),
    enabled: !disabled && userId != null && userId > 0,
  });
};

/**
 * PUT /user/:userId/employment
 */
export const usePutUserEmployment = (options?: {
  onSuccess?: (data: PutUserEmployment["response"]) => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationKey: ["put-user-employment"],
    mutationFn: ({
      userId,
      body,
    }: {
      userId: number;
      body: PutUserEmployment["body"];
    }) => put<PutUserEmployment>(`/user/${userId}/employment`, body),
    displayNotification: options?.displayNotification,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["user-employment", variables.userId],
      });
      options?.onSuccess?.(data);
    },
  });
};

/**
 * POST /user/:userId/cost-rate
 */
export const usePostUserCostRate = (options?: {
  onSuccess?: (data: PostUserCostRate["response"]) => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationKey: ["post-user-cost-rate"],
    mutationFn: ({
      userId,
      body,
    }: {
      userId: number;
      body: PostUserCostRate["body"];
    }) => post<PostUserCostRate>(`/user/${userId}/cost-rate`, body),
    displayNotification: options?.displayNotification,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["user-employment", variables.userId],
      });
      options?.onSuccess?.(data);
    },
  });
};
